import { useApiFpCartRequestCache } from '@hooks-api/fortune/useApiFpCartRequestCache'
import { type CampaignCalculateRequest } from '@hooks-api/types/apiRequest'
import { create } from 'zustand'
import { type FpB2bCartValues } from '../types'
import { Platform } from './fp-b2b/store-map'
import { useFpB2bCartService } from './fp-b2b/use-fp-b2b-cart-service'

export const useStmSyncCartSessionStore = create<{
  syncedCampaignCodes: Set<string>
  addSyncedCampaignCode: (campaignCode: string) => void
  addSyncedCampaignCodes: (campaignCodes: string[]) => void
  resetSyncedCampaignCodes: () => void
}>()(set => ({
  syncedCampaignCodes: new Set<string>(),
  addSyncedCampaignCode: (campaignCode: string) =>
    set(state => ({ syncedCampaignCodes: new Set([...state.syncedCampaignCodes, campaignCode]) })),
  addSyncedCampaignCodes: (campaignCodes: string[]) =>
    set(state => ({ syncedCampaignCodes: new Set([...state.syncedCampaignCodes, ...campaignCodes]) })),
  resetSyncedCampaignCodes: () => set({ syncedCampaignCodes: new Set<string>() }),
}))

export interface UseStmSyncCartSessionToStoreProps {
  campaignCode: string | undefined
  campaignId: number | null
}

/** 共用同一支 cart session API（useApiFpCartRequestCache），但使用獨立的 store 和 service */
export const useStmSyncCartSessionToStore = (args: UseStmSyncCartSessionToStoreProps) => {
  const { campaignCode, campaignId } = args
  const { syncCartStoreFromSession } = useFpB2bCartService(campaignId, Platform.STM)
  const { syncedCampaignCodes, addSyncedCampaignCode } = useStmSyncCartSessionStore()
  const hasFinishedSyncCartSession = campaignCode ? syncedCampaignCodes.has(campaignCode) : false

  // 已同步過就不再拉 session：CSV 提交成功後前端會直接寫本機並標成已同步，
  // 此時 session 裡可能還是舊快照，再拉一次會把剛寫的本機蓋掉
  useApiFpCartRequestCache(hasFinishedSyncCartSession ? null : campaignCode ?? null, {
    shouldRetryOnError: false,
    onError: () => {
      if (campaignCode) addSyncedCampaignCode(campaignCode)
    },
    onSuccess: (data) => {
      const cache = data.data.data as CampaignCalculateRequest<FpB2bCartValues> | null
      syncCartStoreFromSession({
        cartValues: cache?.cart_values,
        orderItems: cache?.order_items,
        callback: () => {
          if (campaignCode) addSyncedCampaignCode(campaignCode)
        },
      })
    },
  })

  return { hasFinishedSyncCartSession }
}
